import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import VAProfileAccountsSection from './VAProfileAccountsSection';
import VAGuidesSection from './VAGuidesSection';
import VAAgentsSection from './VAAgentsSection';
import {
  Squares2X2Icon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  BookOpenIcon,
  TrashIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  Cog6ToothIcon,
  SignalIcon
} from '@heroicons/react/24/outline';
import WarmupProgressTab from './WarmupProgressTab';
import VAFollowbackTab from './VAFollowbackTab';

const VA_SUB_TABS = [
  { id: 'overview', label: 'Overview', icon: Squares2X2Icon },
  { id: 'ai-va', label: 'AI VA', icon: SparklesIcon },
  { id: 'followback', label: 'Followback', icon: SignalIcon },
  { id: 'warmup', label: 'Warmup', icon: ComputerDesktopIcon },
  { id: 'accounts', label: 'Accounts', icon: ClipboardDocumentListIcon },
  { id: 'guides', label: 'Guides', icon: BookOpenIcon }
];

const OVERVIEW_PERIOD_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'custom', label: 'Custom' },
  { id: 'all', label: 'All' }
];

function computeFollowBackPct(follows, followBacks) {
  if (!follows || follows <= 0) return null;
  return Math.round((followBacks / follows) * 10000) / 100;
}

function formatDelta(value) {
  if (value > 0) return `+${value.toLocaleString()}`;
  return value.toLocaleString();
}

function formatFollowBackPct(pct) {
  if (pct == null || Number.isNaN(pct)) return '—';
  return `${pct.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
}

/** Always show numeric counts — 0 means zero activity, not missing data. */
function formatCount(value) {
  return (Number(value) || 0).toLocaleString();
}

function formatRelativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}


function vaAccessRevoked(status) {
  return status === 'suspended' || status === 'frozen';
}

function VaAccessBadge({ status }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        Active
      </span>
    );
  }
  if (vaAccessRevoked(status)) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
        <NoSymbolIcon className="w-3 h-3" />
        Revoked
      </span>
    );
  }
  if (status === 'pending_approval') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
        Pending
      </span>
    );
  }
  return <span className="text-xs text-gray-400">—</span>;
}

const VAsTab = () => {
  const [subTab, setSubTab] = useState('overview');
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [vaUsers, setVaUsers] = useState([]);
  const [accountStatus, setAccountStatus] = useState({ overall: { active: 0, suspended: 0, total: 0 }, byVa: [] });
  const [perVaStats, setPerVaStats] = useState([]);
  const [aiAgentStats, setAiAgentStats] = useState({ items: [], totals: null });
  const [overviewPeriod, setOverviewPeriod] = useState('today');
  const [overviewPeriodLabel, setOverviewPeriodLabel] = useState('today');
  const [overviewCustomFrom, setOverviewCustomFrom] = useState('');
  const [overviewCustomTo, setOverviewCustomTo] = useState('');
  const [overviewScrapeTag, setOverviewScrapeTag] = useState('all');
  const [scrapeTagOptions, setScrapeTagOptions] = useState({
    tags: [],
    untaggedCount: 0,
    totalFollowed: 0,
  });
  const [loading, setLoading] = useState(true);

  /** Row selection for human-VA bulk actions (revoke / delete). */
  const [selectedOverviewRowKeys, setSelectedOverviewRowKeys] = useState(() => new Set());
  const [bulkVaActionLoading, setBulkVaActionLoading] = useState(false);

  const openAgentDetail = useCallback((agentId, automationType, targetTab = null) => {
    const id = String(agentId);
    setSelectedAgentId(id);
    if (targetTab) {
      setSubTab(targetTab);
      return;
    }
    if (automationType === 'warmup') setSubTab('warmup');
    else setSubTab('followback');
  }, []);

  const openAgentConfigure = useCallback((agentId) => {
    setSelectedAgentId(String(agentId));
    setSubTab('ai-va');
  }, []);

  const fetchVaUsers = useCallback(async () => {
    const res = await api.get('/admin/va/users');
    if (res.data.success) setVaUsers(res.data.users || []);
  }, []);

  const fetchAccountStatusSummary = useCallback(async () => {
    const res = await api.get('/admin/va/profile-accounts/status-summary');
    if (res.data.success) {
      setAccountStatus({
        overall: res.data.overall || { active: 0, suspended: 0, total: 0 },
        byVa: res.data.byVa || []
      });
    }
  }, []);

  const fetchPerVaStats = useCallback(async () => {
    try {
      const params = { period: overviewPeriod };
      if (overviewPeriod === 'custom') {
        if (!overviewCustomFrom || !overviewCustomTo) return;
        params.from = overviewCustomFrom;
        params.to = overviewCustomTo;
      }
      const res = await api.get('/admin/va/profile-accounts/stats', { params });
      if (res.data.success) {
        setPerVaStats(res.data.byVa || []);
        setOverviewPeriodLabel(res.data.period?.label || overviewPeriod);
      }
    } catch (_) { /* ignore */ }
  }, [overviewPeriod, overviewCustomFrom, overviewCustomTo]);

  /**
   * Lifetime per-AI-VA-Agent rollup — totals across every job each agent has
   * ever run. Shown on the Overview tab so an admin can compare AI VA Agents
   * at a glance the same way the existing "Per VA overview" table compares
   * human VAs.
   */
  const fetchAiAgentStats = useCallback(async () => {
    try {
      const params = { period: overviewPeriod };
      if (overviewPeriod === 'custom') {
        if (!overviewCustomFrom || !overviewCustomTo) return;
        params.from = overviewCustomFrom;
        params.to = overviewCustomTo;
      }
      if (overviewScrapeTag && overviewScrapeTag !== 'all') {
        params.scrapeTag = overviewScrapeTag;
      }
      const res = await api.get('/admin/va/ai-agents/stats/lifetime', { params });
      if (res.data.success) {
        setAiAgentStats({ items: res.data.items || [], totals: res.data.totals || null });
      }
    } catch (_) { /* the endpoint is admin-only; tolerate failures */ }
  }, [overviewPeriod, overviewCustomFrom, overviewCustomTo, overviewScrapeTag]);

  const fetchScrapeTagOptions = useCallback(async () => {
    try {
      const params = { period: overviewPeriod };
      if (overviewPeriod === 'custom') {
        if (!overviewCustomFrom || !overviewCustomTo) return;
        params.from = overviewCustomFrom;
        params.to = overviewCustomTo;
      }
      const ledgerRes = await api.get('/admin/va/ai-agents/scrape-tags', { params }).catch(() => null);
      const tags = Array.isArray(ledgerRes?.data?.tags) ? ledgerRes.data.tags : [];
      const untaggedCount = ledgerRes?.data?.untaggedCount || 0;
      const totalFollowed = ledgerRes?.data?.totalFollowed || 0;
      setScrapeTagOptions({
        tags: tags.map((t) => ({
          name: t.name,
          count: Number(t.count) || 0,
          followBacks: Number(t.followBacks) || 0,
        })),
        untaggedCount,
        totalFollowed,
      });
    } catch (_) { /* optional */ }
  }, [overviewPeriod, overviewCustomFrom, overviewCustomTo]);

  const handleAccountsStatusChange = useCallback(() => {
    fetchAccountStatusSummary();
  }, [fetchAccountStatusSummary]);

  // Shell load only — don't block AI VA / other tabs on heavy overview stats.
  useEffect(() => {
    fetchVaUsers().catch(() => {});
  }, [fetchVaUsers]);

  useEffect(() => {
    if (subTab === 'accounts') {
      fetchAiAgentStats().catch(() => {});
      return undefined;
    }
    if (subTab !== 'overview') return undefined;

    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchAccountStatusSummary(),
      fetchPerVaStats(),
      fetchAiAgentStats(),
      fetchScrapeTagOptions(),
    ])
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [
    subTab,
    fetchAccountStatusSummary,
    fetchPerVaStats,
    fetchAiAgentStats,
    fetchScrapeTagOptions,
    overviewPeriod,
    overviewCustomFrom,
    overviewCustomTo,
    overviewScrapeTag,
  ]);

  // Human VA rows — for bulk-action selection + human-VA-specific table.
  const perVaOverviewRows = useMemo(() => {
    const statusByVaId = Object.fromEntries(accountStatus.byVa.map((row) => [row.vaId, row]));
    const statsByVaId = Object.fromEntries(perVaStats.map((row) => [row.vaId, row]));
    const allVaIds = new Set([
      ...vaUsers.map((va) => String(va._id)),
      ...accountStatus.byVa.map((r) => r.vaId),
      ...perVaStats.map((r) => r.vaId)
    ]);
    return Array.from(allVaIds)
      .map((vaId) => {
        const status = statusByVaId[vaId];
        const stats = statsByVaId[vaId];
        const va = status?.va || stats?.va || vaUsers.find((v) => String(v._id) === vaId);
        return {
          kind: 'va',
          key: `va:${vaId}`,
          entityId: vaId,
          vaId,
          va,
          label: va?.vaProfile?.displayName || va?.username || vaId,
          accessStatus: va?.status || 'active',
          active: status?.active ?? 0,
          suspended: status?.suspended ?? 0,
          total: status?.total ?? stats?.accountCount ?? 0,
          totalFollowing: stats?.totalFollowing ?? 0,
          followingDelta: stats?.followingDelta ?? 0,
          totalFollowers: stats?.totalFollowers ?? 0,
          followersDelta: stats?.followersDelta ?? 0,
          followBackPct: stats?.followBackPct ?? null,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [accountStatus.byVa, perVaStats, vaUsers]);

  // AI Agent rows — period stats from ledger; scanner + health from enriched API.
  const agentOverviewRows = useMemo(() => (aiAgentStats.items || [])
    .map((agent) => ({
      key: `agent:${agent._id}`,
      entityId: String(agent._id),
      label: agent.name,
      online: !!agent.online,
      runStatus: agent.status || 'idle',
      automationType: agent.automationType || 'follow',
      currentJobId: agent.currentJobId || null,
      accountCount: agent.accountCount || 0,
      activeAccounts: agent.activeAccounts || 0,
      suspendedAccounts: agent.suspendedAccounts || 0,
      authExpiredAccounts: agent.authExpiredAccounts || 0,
      // All-time totals from job aggregate
      lifetimeFollowed: agent.totalFollowed || 0,
      lifetimeFailed: agent.totalFailed || 0,
      periodFollows: agent.periodFollows || 0,
      periodFailed: agent.periodFailed || 0,
      periodFollowBacks: agent.periodFollowBacks || 0,
      periodDmsSent: agent.periodDmsSent || 0,
      followBackPct: agent.followBackPct ?? null,
      pendingLedger: agent.pendingLedger || 0,
      lastPhase1At: agent.followBackHealth?.lastPhase1At || null,
      lastPhase2At: agent.followBackHealth?.lastPhase2At || null,
      scanner: agent.scanner || null,
      liveBatch: agent.liveBatch || null,
      warmup: agent.warmup || null,
      totalFollowing: agent.profileTotals?.following || 0,
      totalFollowers: agent.profileTotals?.followers || 0,
    }))
    .sort((a, b) => b.lifetimeFollowed - a.lifetimeFollowed),
  [aiAgentStats.items]);

  const toggleOverviewRowSelection = (rowKey) => {
    const key = String(rowKey);
    setSelectedOverviewRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllOverviewRowSelection = () => {
    const selectableKeys = perVaOverviewRows.map((row) => row.key);
    if (!selectableKeys.length) return;
    const allSelected = selectableKeys.every((key) => selectedOverviewRowKeys.has(key));
    setSelectedOverviewRowKeys(allSelected ? new Set() : new Set(selectableKeys));
  };

  const refreshVaManagement = useCallback(async () => {
    await Promise.all([fetchVaUsers(), fetchPerVaStats(), fetchAccountStatusSummary(), fetchAiAgentStats()]);
  }, [fetchVaUsers, fetchPerVaStats, fetchAccountStatusSummary, fetchAiAgentStats]);

  /** Only human-VA selections are eligible for the user-management bulk actions. */
  const selectedVaIds = useMemo(
    () => Array.from(selectedOverviewRowKeys)
      .filter((k) => k.startsWith('va:'))
      .map((k) => k.slice(3)),
    [selectedOverviewRowKeys]
  );

  const handleBulkRevokeAccess = async () => {
    if (!selectedVaIds.length) return;
    if (!window.confirm(
      `Revoke WingR portal access for ${selectedVaIds.length} selected VA(s)? They will not be able to log in until access is restored.`
    )) return;
    setBulkVaActionLoading(true);
    try {
      const res = await api.post('/admin/va/users/bulk-revoke-access', { vaIds: selectedVaIds });
      toast.success(res.data.message || 'Access revoked');
      setSelectedOverviewRowKeys(new Set());
      await refreshVaManagement();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to revoke access');
    } finally {
      setBulkVaActionLoading(false);
    }
  };

  const handleBulkRestoreAccess = async () => {
    if (!selectedVaIds.length) return;
    if (!window.confirm(`Restore WingR portal access for ${selectedVaIds.length} selected VA(s)?`)) return;
    setBulkVaActionLoading(true);
    try {
      const res = await api.post('/admin/va/users/bulk-restore-access', { vaIds: selectedVaIds });
      toast.success(res.data.message || 'Access restored');
      setSelectedOverviewRowKeys(new Set());
      await refreshVaManagement();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to restore access');
    } finally {
      setBulkVaActionLoading(false);
    }
  };

  const handleBulkDeleteVas = async () => {
    if (!selectedVaIds.length) return;
    if (!window.confirm(
      `Permanently delete ${selectedVaIds.length} selected VA(s)?\n\nThis removes their login, profile accounts, work history, and schedules. This cannot be undone.`
    )) return;
    setBulkVaActionLoading(true);
    try {
      const res = await api.post('/admin/va/users/bulk-delete', { vaIds: selectedVaIds });
      if (res.data.failed?.length) {
        toast.error(res.data.message || `${res.data.failed.length} delete(s) failed`);
      } else {
        toast.success(res.data.message || 'VAs deleted');
      }
      setSelectedOverviewRowKeys(new Set());
      await refreshVaManagement();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete VAs');
    } finally {
      setBulkVaActionLoading(false);
    }
  };

  const handleRevokeVaAccess = async (row) => {
    const label = row.va?.vaProfile?.displayName || row.va?.username || row.vaId;
    if (!window.confirm(`Revoke portal access for ${label}?`)) return;
    setBulkVaActionLoading(true);
    try {
      await api.post('/admin/va/users/bulk-revoke-access', { vaIds: [String(row.vaId)] });
      toast.success('Access revoked');
      await refreshVaManagement();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to revoke access');
    } finally {
      setBulkVaActionLoading(false);
    }
  };

  const handleRestoreVaAccess = async (row) => {
    const label = row.va?.vaProfile?.displayName || row.va?.username || row.vaId;
    if (!window.confirm(`Restore portal access for ${label}?`)) return;
    setBulkVaActionLoading(true);
    try {
      await api.post('/admin/va/users/bulk-restore-access', { vaIds: [String(row.vaId)] });
      toast.success('Access restored');
      await refreshVaManagement();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to restore access');
    } finally {
      setBulkVaActionLoading(false);
    }
  };

  const handleDeleteVa = async (row) => {
    const label = row.va?.vaProfile?.displayName || row.va?.username || row.vaId;
    if (!window.confirm(
      `Permanently delete ${label}? This removes their login, profile accounts, and all VA data.`
    )) return;
    setBulkVaActionLoading(true);
    try {
      await api.delete(`/admin/va/users/${row.vaId}`);
      toast.success('VA deleted');
      await refreshVaManagement();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete VA');
    } finally {
      setBulkVaActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub-navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-2 shadow-sm overflow-x-auto">
        <div className="flex flex-nowrap gap-2 min-w-max">
          {VA_SUB_TABS.map((t) => {
            const Icon = t.icon;
            const active = subTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSubTab(t.id)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl whitespace-nowrap transition-all ${
                  active
                    ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md shadow-indigo-500/20'
                    : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-white' : 'text-indigo-500'}`} />
                <span className="text-sm font-semibold">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {subTab === 'overview' && loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading overview…</p>
          </div>
        </div>
      )}

      {subTab === 'overview' && !loading && (
        <div className="space-y-6">
          <OverviewPanel
            title="AI VA Agents"
            subtitle="Fleet summary — click a row for follow-back / warmup details"
            icon={SparklesIcon}
            accent="indigo"
          >
              {aiAgentStats.items.length === 0 ? (
                <div className="text-sm text-gray-500 text-center py-4">
                  No AI VA Agents created yet. Head to the <span className="font-medium">AI VA</span> tab to add one.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <MiniStat
                      label="Follows (period)"
                      value={aiAgentStats.totals?.periodFollows ?? 0}
                      hint="ledger · selected period"
                      variant="emerald"
                    />
                    <MiniStat
                      label="Follow-backs"
                      value={aiAgentStats.totals?.periodFollowBacks ?? 0}
                      hint={aiAgentStats.totals?.followBackPct != null ? `${formatFollowBackPct(aiAgentStats.totals.followBackPct)} rate` : 'ledger detected'}
                      variant="indigo"
                    />
                    <MiniStat
                      label="Pending checks"
                      value={aiAgentStats.totals?.pendingLedger ?? 0}
                      hint="awaiting P2 match"
                      variant="amber"
                    />
                    <MiniStat
                      label="Lifetime follows"
                      value={aiAgentStats.totals?.totalFollowed ?? 0}
                      hint="all job runs"
                      variant="slate"
                    />
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200/80 dark:border-gray-700/80">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Per agent — click to open details
                    </div>
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                      {aiAgentStats.items.map((row) => (
                        <button
                          key={row._id}
                          type="button"
                          onClick={() => openAgentDetail(row._id, row.automationType || 'follow')}
                          className="w-full flex items-center justify-between gap-3 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-900/40 transition-colors text-left"
                          title="Open agent details"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`h-2 w-2 rounded-full flex-shrink-0 ${
                              row.online ? 'bg-emerald-500' : 'bg-gray-400'
                            }`} />
                            <span className="font-medium text-gray-900 dark:text-white truncate">{row.name}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${aiAgentRunStatusClass(row.status)}`}>
                              {row.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs flex-shrink-0 tabular-nums">
                            <span className="text-emerald-600 dark:text-emerald-400">
                              {formatCount(row.periodFollows)} sent
                            </span>
                            <span className="text-indigo-600 dark:text-indigo-400">
                              {formatCount(row.periodFollowBacks)} FB
                            </span>
                            {(row.pendingLedger || 0) > 0 && (
                              <span className="text-amber-600 dark:text-amber-400">
                                {formatCount(row.pendingLedger)} pending
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </OverviewPanel>

          {/* Period + category picker — shared by tables below */}
          {(agentOverviewRows.length > 0 || perVaOverviewRows.length > 0) && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1 font-medium uppercase tracking-wide">Period</span>
              {OVERVIEW_PERIOD_OPTIONS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setOverviewPeriod(p.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    overviewPeriod === p.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {overviewPeriod === 'custom' && (
                <>
                  <input type="date" value={overviewCustomFrom} onChange={(e) => setOverviewCustomFrom(e.target.value)} className="form-input text-xs py-1" />
                  <input type="date" value={overviewCustomTo} onChange={(e) => setOverviewCustomTo(e.target.value)} className="form-input text-xs py-1" />
                </>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 mr-1 font-medium uppercase tracking-wide">Category</span>
              <select
                value={overviewScrapeTag}
                onChange={(e) => setOverviewScrapeTag(e.target.value)}
                className="form-input text-xs py-1 max-w-[16rem]"
                title="Filter AI VA follows / follow-backs by scrape data category"
              >
                <option value="all">
                  All categories ({Number(scrapeTagOptions.totalFollowed || 0).toLocaleString()})
                </option>
                <option value="__untagged__">
                  Untagged / legacy ({Number(scrapeTagOptions.untaggedCount || 0).toLocaleString()})
                </option>
                {(scrapeTagOptions.tags || []).map((tag) => (
                  <option key={tag.name} value={tag.name}>
                    {tag.name} ({Number(tag.count || 0).toLocaleString()})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ── AI Agents table ──────────────────────────────────────────── */}
          {agentOverviewRows.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm overflow-x-auto">
              <div className="flex items-center gap-2 mb-3">
                <SparklesIcon className="w-4 h-4 text-indigo-500" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  AI VA Agents
                </h4>
                <span className="text-xs text-gray-400">· {overviewPeriodLabel} · ledger stats · click row for details</span>
              </div>
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
                  <tr>
                    <th className="text-left py-2 pr-4 font-medium">Agent</th>
                    <th className="text-left py-2 pr-4 font-medium">Automation</th>
                    <th className="text-left py-2 pr-4 font-medium">Status</th>
                    <th className="text-left py-2 pr-4 font-medium">Accounts</th>
                    <th className="text-right py-2 pr-4 font-medium text-emerald-600 dark:text-emerald-400" title="Follows recorded in ledger for this period">Follows sent</th>
                    <th className="text-right py-2 pr-4 font-medium text-sky-600 dark:text-sky-400" title="Current job batch progress (live)">Live batch</th>
                    <th className="text-right py-2 pr-4 font-medium text-red-500">Failed</th>
                    <th className="text-right py-2 pr-4 font-medium text-indigo-600 dark:text-indigo-400">Follow-backs</th>
                    <th className="text-right py-2 pr-4 font-medium text-indigo-600 dark:text-indigo-400">FB rate</th>
                    <th className="text-right py-2 pr-4 font-medium text-amber-600 dark:text-amber-400" title="Ledger rows awaiting P2 match">Pending</th>
                    <th className="text-left py-2 pr-4 font-medium" title="Follow-back scanner">Scanner</th>
                    <th className="text-left py-2 pr-4 font-medium" title="Last live Phase 1 pass">Last P1</th>
                    <th className="text-left py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
                  {agentOverviewRows.map((row) => {
                    const isWarmup = row.automationType === 'warmup';
                    const scannerActive = row.scanner?.active;
                    const scannerLabel = scannerActive
                      ? (row.scanner?.scanning ? 'Scanning' : 'Active')
                      : 'Off';
                    const lastP1 = row.lastPhase1At || row.scanner?.lastScanAt;

                    return (
                      <tr
                        key={row.key}
                        className="hover:bg-gray-50/60 dark:hover:bg-gray-900/20 transition-colors cursor-pointer"
                        onClick={() => openAgentDetail(row.entityId, row.automationType)}
                        title="Click to open agent details"
                      >
                        <td className="py-2.5 pr-4 font-medium">
                          <div className="flex items-center gap-1.5">
                            <span className={`h-2 w-2 rounded-full flex-shrink-0 ${row.online ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`} title={row.online ? 'Desktop online' : 'Desktop offline'} />
                            <span className="text-gray-900 dark:text-white">{row.label}</span>
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">AI</span>
                          </div>
                          {row.lifetimeFollowed > 0 && (
                            <div className="text-[11px] text-gray-400 mt-0.5 ml-3.5" title="Lifetime follows sent across all runs">
                              {formatCount(row.lifetimeFollowed)} lifetime
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 pr-4">
                          {isWarmup ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                              Warmup
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                              Follow
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.runStatus === 'running'
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            <ComputerDesktopIcon className="w-3 h-3" />
                            {row.online ? (row.runStatus === 'running' ? 'Running' : 'Online') : 'Offline'}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 tabular-nums">
                          {isWarmup && row.warmup ? (
                            <span className="text-gray-700 dark:text-gray-300" title="Warmup program accounts">
                              {formatCount(row.warmup.total)} in program
                              <span className="text-gray-400 mx-1">·</span>
                              <span className="text-emerald-600">{formatCount(row.warmup.completed)} done</span>
                            </span>
                          ) : (
                            <>
                              <span className="text-gray-700 dark:text-gray-300">{formatCount(row.accountCount)}</span>
                              <span className="text-gray-400 mx-1">·</span>
                              <span className="text-emerald-600 dark:text-emerald-400" title="Active accounts">{row.activeAccounts}</span>
                              {row.suspendedAccounts > 0 && (
                                <>
                                  <span className="text-gray-400 mx-1">·</span>
                                  <span className="text-red-500" title="Suspended accounts">{row.suspendedAccounts} susp</span>
                                </>
                              )}
                              {row.authExpiredAccounts > 0 && (
                                <>
                                  <span className="text-gray-400 mx-1">·</span>
                                  <span className="text-amber-500 dark:text-amber-400" title="Auth expired">{row.authExpiredAccounts} auth exp</span>
                                </>
                              )}
                            </>
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">
                          <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                            {formatCount(row.periodFollows)}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">
                          {row.liveBatch ? (
                            <span className="text-sky-600 dark:text-sky-400 font-medium" title={`Job ${row.liveBatch.jobStatus}`}>
                              {formatCount(row.liveBatch.followed)}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-red-500">
                          {formatCount(row.periodFailed)}
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-indigo-600 dark:text-indigo-400 font-medium">
                          {formatCount(row.periodFollowBacks)}
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-indigo-600 dark:text-indigo-400">
                          {row.followBackPct != null ? formatFollowBackPct(row.followBackPct) : '—'}
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-amber-600 dark:text-amber-400">
                          {formatCount(row.pendingLedger)}
                        </td>
                        <td className="py-2.5 pr-4">
                          {!isWarmup ? (
                            <span className={`inline-flex items-center gap-1 text-xs ${scannerActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                              <SignalIcon className="w-3 h-3" />
                              {scannerLabel}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {!isWarmup ? formatRelativeTime(lastP1) : (row.warmup?.lastRunDate || '—')}
                        </td>
                        <td className="py-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => openAgentDetail(row.entityId, row.automationType)}
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                            >
                              Details
                            </button>
                            <button
                              type="button"
                              onClick={() => openAgentConfigure(row.entityId)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400"
                              title="Open AI VA configuration"
                            >
                              <Cog6ToothIcon className="w-3.5 h-3.5" />
                              Configure
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {agentOverviewRows.length > 1 && (() => {
                  const t = aiAgentStats.totals || {};
                  return (
                    <tfoot className="border-t border-gray-200 dark:border-gray-600">
                      <tr className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                        <td className="py-2 pr-4" colSpan={4}>Total ({agentOverviewRows.length} agents)</td>
                        <td className="py-2 pr-4 text-right text-emerald-600 dark:text-emerald-400 tabular-nums">{formatCount(t.periodFollows)}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">—</td>
                        <td className="py-2 pr-4 text-right text-red-500 tabular-nums">{formatCount(t.periodFailed)}</td>
                        <td className="py-2 pr-4 text-right text-indigo-600 dark:text-indigo-400 tabular-nums">{formatCount(t.periodFollowBacks)}</td>
                        <td className="py-2 pr-4 text-right text-indigo-600 dark:text-indigo-400 tabular-nums">
                          {t.periodFollows > 0 ? formatFollowBackPct(t.followBackPct ?? computeFollowBackPct(t.periodFollows, t.periodFollowBacks)) : '—'}
                        </td>
                        <td className="py-2 pr-4 text-right text-amber-600 dark:text-amber-400 tabular-nums">{formatCount(t.pendingLedger)}</td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          )}

          {/* ── Human VAs table ──────────────────────────────────────────── */}
          {perVaOverviewRows.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm overflow-x-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="w-4 h-4 text-gray-500" />
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Human VAs</h4>
                  <span className="text-xs text-gray-400">· {overviewPeriodLabel} · stats from profile scans</span>
                </div>
                {selectedVaIds.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">{selectedVaIds.length} selected</span>
                    <button type="button" onClick={handleBulkRevokeAccess} disabled={bulkVaActionLoading} className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 !px-3 text-sm text-amber-700 dark:text-amber-300 disabled:opacity-50">
                      <NoSymbolIcon className="w-4 h-4" />
                      Revoke access
                    </button>
                    <button type="button" onClick={handleBulkRestoreAccess} disabled={bulkVaActionLoading} className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 !px-3 text-sm text-emerald-700 dark:text-emerald-300 disabled:opacity-50">
                      <CheckCircleIcon className="w-4 h-4" />
                      Restore access
                    </button>
                    <button type="button" onClick={handleBulkDeleteVas} disabled={bulkVaActionLoading} className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 !px-3 text-sm text-red-700 dark:text-red-300 disabled:opacity-50">
                      <TrashIcon className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
                  <tr>
                    <th className="text-left py-2 pr-2 w-8 font-medium">
                      <input
                        type="checkbox"
                        checked={perVaOverviewRows.length > 0 && perVaOverviewRows.every((r) => selectedOverviewRowKeys.has(r.key))}
                        onChange={toggleAllOverviewRowSelection}
                        className="rounded"
                        title="Select all"
                      />
                    </th>
                    <th className="text-left py-2 pr-4 font-medium">VA</th>
                    <th className="text-left py-2 pr-4 font-medium">Portal access</th>
                    <th className="text-right py-2 pr-4 font-medium">Accounts</th>
                    <th className="text-right py-2 pr-4 font-medium text-emerald-600 dark:text-emerald-400">Active</th>
                    <th className="text-right py-2 pr-4 font-medium text-red-500">Suspended</th>
                    <th className="text-right py-2 pr-4 font-medium">Following</th>
                    <th className="text-right py-2 pr-4 font-medium">Δ Following</th>
                    <th className="text-right py-2 pr-4 font-medium">Follow-back %</th>
                    <th className="text-left py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
                  {perVaOverviewRows.map((row) => (
                    <tr key={row.key} className="hover:bg-gray-50/60 dark:hover:bg-gray-900/20 transition-colors">
                      <td className="py-2.5 pr-2">
                        <input
                          type="checkbox"
                          checked={selectedOverviewRowKeys.has(row.key)}
                          onChange={() => toggleOverviewRowSelection(row.key)}
                          className="rounded"
                        />
                      </td>
                      <td className="py-2.5 pr-4 font-medium text-gray-900 dark:text-white">{row.label}</td>
                      <td className="py-2.5 pr-4"><VaAccessBadge status={row.accessStatus} /></td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">{row.total}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-emerald-600 dark:text-emerald-400">{row.active}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-red-500">{row.suspended > 0 ? row.suspended : <span className="text-gray-300 dark:text-gray-600">—</span>}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">{(row.totalFollowing || 0).toLocaleString()}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-emerald-600 dark:text-emerald-400">{formatDelta(row.followingDelta || 0)}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-indigo-600 dark:text-indigo-400">{formatFollowBackPct(row.followBackPct)}</td>
                      <td className="py-2.5">
                        <div className="flex flex-wrap gap-1.5">
                          {vaAccessRevoked(row.accessStatus) ? (
                            <button type="button" onClick={() => handleRestoreVaAccess(row)} disabled={bulkVaActionLoading} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 disabled:opacity-50">
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              Restore
                            </button>
                          ) : (
                            <button type="button" onClick={() => handleRevokeVaAccess(row)} disabled={bulkVaActionLoading || row.accessStatus !== 'active'} className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400 disabled:opacity-50">
                              <NoSymbolIcon className="w-3.5 h-3.5" />
                              Revoke
                            </button>
                          )}
                          <button type="button" onClick={() => handleDeleteVa(row)} disabled={bulkVaActionLoading} className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-50">
                            <TrashIcon className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Agent category views ──────────────────────────────────────── */}
          {agentOverviewRows.length > 0 && (() => {
            const followAgents  = agentOverviewRows.filter((r) => r.automationType !== 'warmup' && r.automationType !== 'snap_chat');
            const warmupAgents  = agentOverviewRows.filter((r) => r.automationType === 'warmup');
            const snapAgents    = agentOverviewRows.filter((r) => r.automationType === 'snap_chat');
            return (
              <div className="space-y-4">
                {followAgents.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                        Follow Automation
                      </span>
                      <span className="text-xs text-gray-400">{followAgents.length} agent{followAgents.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-2">
                      {followAgents.map((row) => (
                        <div key={row.key} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`h-2 w-2 rounded-full flex-shrink-0 ${row.online ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.label}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${row.runStatus === 'running' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                              {row.runStatus === 'running' ? 'Running' : row.online ? 'Online' : 'Offline'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0 tabular-nums">
                            <span>{row.accountCount.toLocaleString()} accts</span>
                            <span className="text-emerald-600 dark:text-emerald-400">{row.periodFollows.toLocaleString()} follows</span>
                            {row.periodFollowBacks > 0 && <span className="text-indigo-500">{row.periodFollowBacks.toLocaleString()} fb</span>}
                            <button type="button" onClick={() => openAgentConfigure(row.entityId)} className="text-indigo-600 dark:text-indigo-400 hover:underline">Configure</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {warmupAgents.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        Warmup Runner
                      </span>
                      <span className="text-xs text-gray-400">{warmupAgents.length} agent{warmupAgents.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-2">
                      {warmupAgents.map((row) => (
                        <div key={row.key} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`h-2 w-2 rounded-full flex-shrink-0 ${row.online ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.label}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${row.runStatus === 'running' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                              {row.runStatus === 'running' ? 'Warming up' : row.online ? 'Online' : 'Offline'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0 tabular-nums">
                            <span>{row.accountCount.toLocaleString()} accts</span>
                            <button type="button" onClick={() => openAgentConfigure(row.entityId)} className="text-indigo-600 dark:text-indigo-400 hover:underline">Configure</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {snapAgents.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200">
                        Snapchat Chatbot
                      </span>
                      <span className="text-xs text-gray-400">{snapAgents.length} agent{snapAgents.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-2">
                      {snapAgents.map((row) => (
                        <div key={row.key} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`h-2 w-2 rounded-full flex-shrink-0 ${row.online ? 'bg-yellow-400' : 'bg-gray-400'}`} />
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.label}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${row.runStatus === 'running' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                              {row.runStatus === 'running' ? 'Chatting' : row.online ? 'Online' : 'Offline'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0 tabular-nums">
                            <span>{row.accountCount.toLocaleString()} accts</span>
                            <button type="button" onClick={() => openAgentConfigure(row.entityId)} className="text-indigo-600 dark:text-indigo-400 hover:underline">Configure</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {subTab === 'ai-va' && (
        <VAAgentsSection vaUsers={vaUsers} initialAgentId={selectedAgentId} />
      )}

      {subTab === 'followback' && (
        <VAFollowbackTab initialAgentId={selectedAgentId} />
      )}

      {subTab === 'warmup' && (
        <WarmupProgressTab agents={aiAgentStats.items || []} initialAgentId={selectedAgentId} />
      )}

      {subTab === 'accounts' && (
        <VAProfileAccountsSection
          vaUsers={vaUsers}
          aiAgents={aiAgentStats.items}
          onStatusChange={handleAccountsStatusChange}
        />
      )}

      {subTab === 'guides' && (
        <VAGuidesSection />
      )}
    </div>
  );
};

export default VAsTab;

const PANEL_ACCENTS = {
  indigo: {
    border: 'border-indigo-200 dark:border-indigo-800',
    header: 'bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/30',
    icon: 'bg-indigo-500 text-white'
  },
  violet: {
    border: 'border-violet-200 dark:border-violet-800',
    header: 'bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/40 dark:to-purple-950/30',
    icon: 'bg-violet-500 text-white'
  }
};

function OverviewPanel({ title, subtitle, icon: Icon, accent = 'indigo', children }) {
  const styles = PANEL_ACCENTS[accent] || PANEL_ACCENTS.indigo;
  return (
    <div className={`rounded-2xl border shadow-sm overflow-hidden ${styles.border} bg-white dark:bg-gray-800`}>
      <div className={`px-5 py-4 ${styles.header}`}>
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${styles.icon}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{subtitle}</p>
          </div>
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const MINI_VARIANTS = {
  indigo: 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-100 dark:border-indigo-900',
  emerald: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-100 dark:border-emerald-900',
  amber: 'bg-amber-50 dark:bg-amber-950/30 border-amber-100 dark:border-amber-900',
  red: 'bg-red-50 dark:bg-red-950/30 border-red-100 dark:border-red-900',
  slate: 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700'
};

const MINI_VALUE_COLORS = {
  indigo: 'text-indigo-700 dark:text-indigo-300',
  emerald: 'text-emerald-700 dark:text-emerald-300',
  amber: 'text-amber-700 dark:text-amber-300',
  red: 'text-red-700 dark:text-red-300',
  slate: 'text-gray-900 dark:text-white'
};

function aiAgentRunStatusClass(status) {
  switch (status) {
    case 'running': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200';
    case 'error': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200';
    case 'paused': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
}

function MiniStat({ label, value, hint, variant = 'slate' }) {
  return (
    <div className={`rounded-xl border p-3 ${MINI_VARIANTS[variant] || MINI_VARIANTS.slate}`}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${MINI_VALUE_COLORS[variant] || MINI_VALUE_COLORS.slate}`}>
        {Number(value).toLocaleString()}
      </div>
      {hint && <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-500">{hint}</div>}
    </div>
  );
}
